/*
 * Landing-page copy in every language Board Bash supports.
 *
 * The locale list and the display names mirror the app's own picker
 * (`packages/frontend-boardfest/src/main-menu/language-content.tsx` in the
 * boardbash repo). Game names and taglines are taken verbatim from the app's
 * `gameNames` block (`src/i18n/locales/*.ts`) so the portal and the game never
 * disagree on what a game is called.
 *
 * A classic script, not a module: it must run before first paint (see apply.js).
 * Keys missing from a locale fall back to English, but `npm run check:i18n`
 * fails the build on a gap, so they should never be missing.
 */
window.NOCAP_I18N = {
    locales: [
        {code: 'en', name: 'English'},
        {code: 'de', name: 'Deutsch'},
        {code: 'fr', name: 'Français'},
        {code: 'it', name: 'Italiano'},
        {code: 'es', name: 'Español'},
        {code: 'pl', name: 'Polski'},
        {code: 'cs', name: 'Čeština'},
        {code: 'mk', name: 'Македонски'},
    ],

    strings: {
        en: {
            meta: {
                title: 'nocap.gg - Pick a game. Play right now.',
                description: 'Browser games we build ourselves. Quick rounds, real opponents, nothing to install.',
            },
            nav: {aria: 'Main', ourGames: 'Our games'},
            hero: {
                titleLine1: 'Pick a game.',
                titleLine2: 'Play right now.',
                cta: 'See our games',
            },
            games: {
                heading: 'Our games',
            },
            boardbash: {
                logoAlt: 'Board Bash logo',
                tagline: 'Classic board games, fast rounds',
                body: 'Tic-Tac-Toe, Four in a Row, Dots & Boxes and Gomoku. Play against the computer, pass the device around, or open a room and go head-to-head with a friend.',
                tagTicTacToe: 'Tic-Tac-Toe',
                tagFourInARow: 'Four in a Row',
                tagDotsAndBoxes: 'Dots & Boxes',
                tagGomoku: 'Gomoku',
                play: 'Play Board Bash',
                storeAria: 'Get Board Bash on Google Play',
                players: '1–2 players · online & local',
            },
            lagerhaus: {
                tagline: 'Warehouse puzzle',
                body: 'Sokoban, done beautifully. Push every crate onto its mark in as few moves as you can, then take your score to the online leaderboard. Endless unique levels, so no two rounds are alike.',
                tagPuzzle: 'Puzzle',
                tagSolo: 'Solo',
                tagLeaderboards: 'Leaderboards',
                play: 'Play Lagerhaus',
                storeAria: 'Get Lagerhaus on Google Play',
                players: '1 player · web & mobile',
            },
            store: {badgeAlt: 'Get it on Google Play'},
            workshop: {
                heading: 'More of our games are in the workshop.',
                body: 'We only put a game here once we’re proud of it, so new titles land as soon as they’re ready to play.',
            },
            footer: {
                games: 'Games',
                privacy: 'Privacy',
                contact: 'Contact',
                madeWith: 'Made with ❤️ in',
                germanEngineered: 'German-engineered',
                australia: 'Australia',
                legal: 'Google Play and the Google Play logo are trademarks of Google LLC.',
            },
            contact: {
                title: 'Say hi',
                lede: 'Pick what it’s about and your email app opens with the subject filled in. A real person reads every message.',
                close: 'Close',
                feedbackTitle: 'Feedback or ideas',
                feedbackBody: 'What you love, what you’d change',
                bugTitle: 'Something’s broken',
                bugBody: 'Tell us the game and your device',
                dataTitle: 'My data',
                dataBody: 'See or delete what we hold',
                orWrite: 'Or write to us directly:',
                copy: 'Copy',
                copied: 'Copied',
            },
            ui: {languageLabel: 'Language'},
        },

        de: {
            meta: {
                title: 'nocap.gg - Spiel aussuchen. Sofort spielen.',
                description: 'Browsergames, die wir selbst bauen. Kurze Runden, echte Gegner, nichts zu installieren.',
            },
            nav: {aria: 'Hauptnavigation', ourGames: 'Unsere Spiele'},
            hero: {
                titleLine1: 'Spiel aussuchen.',
                titleLine2: 'Sofort spielen.',
                cta: 'Unsere Spiele ansehen',
            },
            games: {
                heading: 'Unsere Spiele',
            },
            boardbash: {
                logoAlt: 'Board Bash Logo',
                tagline: 'Klassische Brettspiele, schnelle Runden',
                body: 'Tic-Tac-Toe, Vier in einer Reihe, Käsekästchen und Gomoku. Spiel gegen den Computer, gib das Gerät weiter, oder öffne einen Raum und tritt gegen Freunde an.',
                tagTicTacToe: 'Tic-Tac-Toe',
                tagFourInARow: 'Vier in einer Reihe',
                tagDotsAndBoxes: 'Käsekästchen',
                tagGomoku: 'Gomoku',
                play: 'Board Bash spielen',
                storeAria: 'Board Bash bei Google Play holen',
                players: '1–2 Spieler · online & lokal',
            },
            lagerhaus: {
                tagline: 'Lager-Puzzle',
                body: 'Sokoban, richtig schön gemacht. Schiebe jede Kiste mit so wenigen Zügen wie möglich auf ihr Ziel und bring deine Punktzahl in die Online-Rangliste. Endlos viele einzigartige Level – keine Runde gleicht der anderen.',
                tagPuzzle: 'Puzzle',
                tagSolo: 'Solo',
                tagLeaderboards: 'Ranglisten',
                play: 'Lagerhaus spielen',
                storeAria: 'Lagerhaus bei Google Play holen',
                players: '1 Spieler · Web & Mobil',
            },
            store: {badgeAlt: 'Jetzt bei Google Play'},
            workshop: {
                heading: 'Weitere Spiele sind in der Werkstatt.',
                body: 'Ein Spiel kommt erst hierher, wenn wir stolz darauf sind – neue Titel erscheinen also, sobald sie spielbereit sind.',
            },
            footer: {
                games: 'Spiele',
                privacy: 'Datenschutz',
                contact: 'Kontakt',
                madeWith: 'Mit ❤️ gemacht in',
                germanEngineered: 'Deutsche Ingenieurskunst',
                australia: 'Australien',
                legal: 'Google Play und das Google Play Logo sind Marken von Google LLC.',
            },
            contact: {
                title: 'Schreib uns',
                lede: 'Wähl ein Thema, und dein E-Mail-Programm öffnet sich mit fertigem Betreff. Jede Nachricht liest ein echter Mensch.',
                close: 'Schließen',
                feedbackTitle: 'Feedback oder Ideen',
                feedbackBody: 'Was dir gefällt, was du ändern würdest',
                bugTitle: 'Etwas funktioniert nicht',
                bugBody: 'Nenn uns das Spiel und dein Gerät',
                dataTitle: 'Meine Daten',
                dataBody: 'Einsehen oder löschen, was wir speichern',
                orWrite: 'Oder schreib uns direkt:',
                copy: 'Kopieren',
                copied: 'Kopiert',
            },
            ui: {languageLabel: 'Sprache'},
        },

        fr: {
            meta: {
                title: 'nocap.gg - Choisissez un jeu. Jouez tout de suite.',
                description: 'Des jeux de navigateur que nous créons nous-mêmes. Parties rapides, vrais adversaires, rien à installer.',
            },
            nav: {aria: 'Navigation principale', ourGames: 'Nos jeux'},
            hero: {
                titleLine1: 'Choisissez un jeu.',
                titleLine2: 'Jouez tout de suite.',
                cta: 'Voir nos jeux',
            },
            games: {
                heading: 'Nos jeux',
            },
            boardbash: {
                logoAlt: 'Logo de Board Bash',
                tagline: 'Jeux de société classiques, parties rapides',
                body: 'Morpion, Quatre alignés, Pipopipette et Gomoku. Jouez contre l’ordinateur, passez l’appareil, ou ouvrez une salle pour affronter un ami.',
                tagTicTacToe: 'Morpion',
                tagFourInARow: 'Quatre alignés',
                tagDotsAndBoxes: 'Pipopipette',
                tagGomoku: 'Gomoku',
                play: 'Jouer à Board Bash',
                storeAria: 'Télécharger Board Bash sur Google Play',
                players: '1–2 joueurs · en ligne et local',
            },
            lagerhaus: {
                tagline: 'Casse-tête d’entrepôt',
                body: 'Sokoban, en beauté. Poussez chaque caisse sur sa marque en un minimum de coups, puis placez votre score au classement en ligne. Des niveaux uniques à l’infini : aucune partie ne ressemble à une autre.',
                tagPuzzle: 'Casse-tête',
                tagSolo: 'Solo',
                tagLeaderboards: 'Classements',
                play: 'Jouer à Lagerhaus',
                storeAria: 'Télécharger Lagerhaus sur Google Play',
                players: '1 joueur · web et mobile',
            },
            store: {badgeAlt: 'Disponible sur Google Play'},
            workshop: {
                heading: 'D’autres de nos jeux sont à l’atelier.',
                body: 'Un jeu n’arrive ici que lorsque nous en sommes fiers : les nouveaux titres sortent dès qu’ils sont prêts à jouer.',
            },
            footer: {
                games: 'Jeux',
                privacy: 'Confidentialité',
                contact: 'Contact',
                madeWith: 'Fait avec ❤️ en',
                germanEngineered: 'Ingénierie allemande',
                australia: 'Australie',
                legal: 'Google Play et le logo Google Play sont des marques de Google LLC.',
            },
            contact: {
                title: 'Écrivez-nous',
                lede: 'Choisissez le sujet et votre messagerie s’ouvre avec l’objet déjà rempli. Une vraie personne lit chaque message.',
                close: 'Fermer',
                feedbackTitle: 'Avis ou idées',
                feedbackBody: 'Ce que vous aimez, ce que vous changeriez',
                bugTitle: 'Quelque chose ne marche pas',
                bugBody: 'Indiquez le jeu et votre appareil',
                dataTitle: 'Mes données',
                dataBody: 'Consulter ou supprimer ce que nous gardons',
                orWrite: 'Ou écrivez-nous directement :',
                copy: 'Copier',
                copied: 'Copié',
            },
            ui: {languageLabel: 'Langue'},
        },

        it: {
            meta: {
                title: 'nocap.gg - Scegli un gioco. Gioca subito.',
                description: 'Giochi da browser che realizziamo noi. Partite veloci, avversari reali, niente da installare.',
            },
            nav: {aria: 'Navigazione principale', ourGames: 'I nostri giochi'},
            hero: {
                titleLine1: 'Scegli un gioco.',
                titleLine2: 'Gioca subito.',
                cta: 'Guarda i nostri giochi',
            },
            games: {
                heading: 'I nostri giochi',
            },
            boardbash: {
                logoAlt: 'Logo di Board Bash',
                tagline: 'Giochi da tavolo classici, partite veloci',
                body: 'Tris, Quattro in fila, Punti e quadrati e Gomoku. Gioca contro il computer, passa il dispositivo, o apri una stanza e sfida un amico.',
                tagTicTacToe: 'Tris',
                tagFourInARow: 'Quattro in fila',
                tagDotsAndBoxes: 'Punti e quadrati',
                tagGomoku: 'Gomoku',
                play: 'Gioca a Board Bash',
                storeAria: 'Scarica Board Bash su Google Play',
                players: '1–2 giocatori · online e locale',
            },
            lagerhaus: {
                tagline: 'Rompicapo in magazzino',
                body: 'Sokoban, fatto bene. Spingi ogni cassa sul suo segno con il minor numero di mosse, poi porta il tuo punteggio nella classifica online. Livelli unici senza fine: nessuna partita è uguale all’altra.',
                tagPuzzle: 'Rompicapo',
                tagSolo: 'Solo',
                tagLeaderboards: 'Classifiche',
                play: 'Gioca a Lagerhaus',
                storeAria: 'Scarica Lagerhaus su Google Play',
                players: '1 giocatore · web e mobile',
            },
            store: {badgeAlt: 'Disponibile su Google Play'},
            workshop: {
                heading: 'Altri nostri giochi sono in officina.',
                body: 'Un gioco arriva qui solo quando ne siamo fieri, così i nuovi titoli escono appena sono pronti da giocare.',
            },
            footer: {
                games: 'Giochi',
                privacy: 'Privacy',
                contact: 'Contatti',
                madeWith: 'Fatto con ❤️ in',
                germanEngineered: 'Ingegneria tedesca',
                australia: 'Australia',
                legal: 'Google Play e il logo Google Play sono marchi di Google LLC.',
            },
            contact: {
                title: 'Scrivici',
                lede: 'Scegli l’argomento e la tua app di posta si apre con l’oggetto già compilato. Ogni messaggio lo legge una persona vera.',
                close: 'Chiudi',
                feedbackTitle: 'Feedback o idee',
                feedbackBody: 'Cosa ti piace, cosa cambieresti',
                bugTitle: 'Qualcosa non funziona',
                bugBody: 'Dicci il gioco e il tuo dispositivo',
                dataTitle: 'I miei dati',
                dataBody: 'Vedi o elimina ciò che conserviamo',
                orWrite: 'Oppure scrivici direttamente:',
                copy: 'Copia',
                copied: 'Copiato',
            },
            ui: {languageLabel: 'Lingua'},
        },

        es: {
            meta: {
                title: 'nocap.gg - Elige un juego. Juega ahora mismo.',
                description: 'Juegos de navegador que hacemos nosotros. Partidas rápidas, rivales reales, nada que instalar.',
            },
            nav: {aria: 'Navegación principal', ourGames: 'Nuestros juegos'},
            hero: {
                titleLine1: 'Elige un juego.',
                titleLine2: 'Juega ahora mismo.',
                cta: 'Ver nuestros juegos',
            },
            games: {
                heading: 'Nuestros juegos',
            },
            boardbash: {
                logoAlt: 'Logotipo de Board Bash',
                tagline: 'Juegos de mesa clásicos, partidas rápidas',
                body: 'Tres en raya, Cuatro en línea, Puntos y cajas y Gomoku. Juega contra la máquina, pasa el dispositivo, o abre una sala y reta a un amigo.',
                tagTicTacToe: 'Tres en raya',
                tagFourInARow: 'Cuatro en línea',
                tagDotsAndBoxes: 'Puntos y cajas',
                tagGomoku: 'Gomoku',
                play: 'Jugar a Board Bash',
                storeAria: 'Consigue Board Bash en Google Play',
                players: '1–2 jugadores · en línea y local',
            },
            lagerhaus: {
                tagline: 'Puzle de almacén',
                body: 'Sokoban, bien hecho. Empuja cada caja hasta su marca con los menos movimientos posibles y lleva tu puntuación a la clasificación en línea. Niveles únicos infinitos: ninguna partida se parece a otra.',
                tagPuzzle: 'Puzle',
                tagSolo: 'Individual',
                tagLeaderboards: 'Clasificaciones',
                play: 'Jugar a Lagerhaus',
                storeAria: 'Consigue Lagerhaus en Google Play',
                players: '1 jugador · web y móvil',
            },
            store: {badgeAlt: 'Disponible en Google Play'},
            workshop: {
                heading: 'Tenemos más juegos en el taller.',
                body: 'Un juego solo llega aquí cuando estamos orgullosos de él, así que los nuevos títulos aparecen en cuanto están listos para jugar.',
            },
            footer: {
                games: 'Juegos',
                privacy: 'Privacidad',
                contact: 'Contacto',
                madeWith: 'Hecho con ❤️ en',
                germanEngineered: 'Ingeniería alemana',
                australia: 'Australia',
                legal: 'Google Play y el logotipo de Google Play son marcas de Google LLC.',
            },
            contact: {
                title: 'Escríbenos',
                lede: 'Elige el tema y tu app de correo se abre con el asunto ya escrito. Cada mensaje lo lee una persona real.',
                close: 'Cerrar',
                feedbackTitle: 'Opiniones o ideas',
                feedbackBody: 'Lo que te gusta, lo que cambiarías',
                bugTitle: 'Algo no funciona',
                bugBody: 'Dinos el juego y tu dispositivo',
                dataTitle: 'Mis datos',
                dataBody: 'Consulta o borra lo que guardamos',
                orWrite: 'O escríbenos directamente:',
                copy: 'Copiar',
                copied: 'Copiado',
            },
            ui: {languageLabel: 'Idioma'},
        },

        pl: {
            meta: {
                title: 'nocap.gg - Wybierz grę. Zagraj od razu.',
                description: 'Gry przeglądarkowe, które robimy sami. Szybkie rundy, prawdziwi przeciwnicy, nic do instalowania.',
            },
            nav: {aria: 'Nawigacja główna', ourGames: 'Nasze gry'},
            hero: {
                titleLine1: 'Wybierz grę.',
                titleLine2: 'Zagraj od razu.',
                cta: 'Zobacz nasze gry',
            },
            games: {
                heading: 'Nasze gry',
            },
            boardbash: {
                logoAlt: 'Logo Board Bash',
                tagline: 'Klasyczne gry planszowe, szybkie rundy',
                body: 'Kółko i krzyżyk, Cztery w rzędzie, Kropki i kwadraty oraz Gomoku. Zagraj z komputerem, podaj urządzenie dalej, albo otwórz pokój i zmierz się ze znajomym.',
                tagTicTacToe: 'Kółko i krzyżyk',
                tagFourInARow: 'Cztery w rzędzie',
                tagDotsAndBoxes: 'Kropki i kwadraty',
                tagGomoku: 'Gomoku',
                play: 'Zagraj w Board Bash',
                storeAria: 'Pobierz Board Bash z Google Play',
                players: '1–2 gracze · online i lokalnie',
            },
            lagerhaus: {
                tagline: 'Łamigłówka magazynowa',
                body: 'Sokoban zrobiony z klasą. Przepchnij każdą skrzynię na jej miejsce w jak najmniejszej liczbie ruchów, a potem wyślij wynik na rankingi online. Nieskończone, unikalne poziomy – żadna runda nie jest taka sama.',
                tagPuzzle: 'Łamigłówka',
                tagSolo: 'Solo',
                tagLeaderboards: 'Rankingi',
                play: 'Zagraj w Lagerhaus',
                storeAria: 'Pobierz Lagerhaus z Google Play',
                players: '1 gracz · web i mobile',
            },
            store: {badgeAlt: 'Pobierz z Google Play'},
            workshop: {
                heading: 'Więcej naszych gier jest w warsztacie.',
                body: 'Gra trafia tutaj dopiero wtedy, gdy jesteśmy z niej dumni – nowe tytuły pojawiają się, gdy są gotowe do gry.',
            },
            footer: {
                games: 'Gry',
                privacy: 'Prywatność',
                contact: 'Kontakt',
                madeWith: 'Zrobione z ❤️ w',
                germanEngineered: 'Niemiecka inżynieria',
                australia: 'Australii',
                legal: 'Google Play i logo Google Play są znakami towarowymi Google LLC.',
            },
            contact: {
                title: 'Napisz do nas',
                lede: 'Wybierz temat, a Twoja aplikacja pocztowa otworzy się z gotowym tytułem wiadomości. Każdą wiadomość czyta prawdziwy człowiek.',
                close: 'Zamknij',
                feedbackTitle: 'Opinie lub pomysły',
                feedbackBody: 'Co Ci się podoba, a co warto zmienić',
                bugTitle: 'Coś nie działa',
                bugBody: 'Podaj grę i swoje urządzenie',
                dataTitle: 'Moje dane',
                dataBody: 'Sprawdź lub usuń to, co przechowujemy',
                orWrite: 'Albo napisz bezpośrednio:',
                copy: 'Kopiuj',
                copied: 'Skopiowano',
            },
            ui: {languageLabel: 'Język'},
        },

        cs: {
            meta: {
                title: 'nocap.gg - Vyber si hru. Hraj hned.',
                description: 'Prohlížečové hry, které tvoříme sami. Krátké partie, skuteční protivníci, nic se neinstaluje.',
            },
            nav: {aria: 'Hlavní navigace', ourGames: 'Naše hry'},
            hero: {
                titleLine1: 'Vyber si hru.',
                titleLine2: 'Hraj hned.',
                cta: 'Prohlédnout naše hry',
            },
            games: {
                heading: 'Naše hry',
            },
            boardbash: {
                logoAlt: 'Logo Board Bash',
                tagline: 'Klasické deskové hry, rychlé partie',
                body: 'Piškvorky, Čtyři v řadě, Tečky a čtverce a Gomoku. Hraj proti počítači, posílej zařízení kolem, nebo otevři místnost a vyzvi přítele.',
                tagTicTacToe: 'Piškvorky',
                tagFourInARow: 'Čtyři v řadě',
                tagDotsAndBoxes: 'Tečky a čtverce',
                tagGomoku: 'Gomoku',
                play: 'Hrát Board Bash',
                storeAria: 'Získat Board Bash na Google Play',
                players: '1–2 hráči · online i lokálně',
            },
            lagerhaus: {
                tagline: 'Skladová hádanka',
                body: 'Sokoban, jak má být. Dostrkej každou bednu na její místo na co nejmenší počet tahů a pak pošli skóre do online žebříčku. Nekonečně unikátních úrovní – žádná partie není stejná.',
                tagPuzzle: 'Hádanka',
                tagSolo: 'Solo',
                tagLeaderboards: 'Žebříčky',
                play: 'Hrát Lagerhaus',
                storeAria: 'Získat Lagerhaus na Google Play',
                players: '1 hráč · web i mobil',
            },
            store: {badgeAlt: 'Nyní na Google Play'},
            workshop: {
                heading: 'Další naše hry jsou v dílně.',
                body: 'Hra se sem dostane teprve tehdy, když jsme na ni pyšní – nové tituly tak přicházejí, jakmile jsou hratelné.',
            },
            footer: {
                games: 'Hry',
                privacy: 'Soukromí',
                contact: 'Kontakt',
                madeWith: 'Vyrobeno s ❤️ v',
                germanEngineered: 'Německé inženýrství',
                australia: 'Austrálii',
                legal: 'Google Play a logo Google Play jsou obchodní známky společnosti Google LLC.',
            },
            contact: {
                title: 'Napiš nám',
                lede: 'Vyber téma a otevře se ti e-mail s vyplněným předmětem. Každou zprávu čte skutečný člověk.',
                close: 'Zavřít',
                feedbackTitle: 'Názory nebo nápady',
                feedbackBody: 'Co se ti líbí a co změnit',
                bugTitle: 'Něco nefunguje',
                bugBody: 'Napiš nám hru a své zařízení',
                dataTitle: 'Moje data',
                dataBody: 'Zobrazit nebo smazat, co uchováváme',
                orWrite: 'Nebo nám napiš přímo:',
                copy: 'Kopírovat',
                copied: 'Zkopírováno',
            },
            ui: {languageLabel: 'Jazyk'},
        },

        mk: {
            meta: {
                title: 'nocap.gg - Избери игра. Играј веднаш.',
                description: 'Игри во прелистувач што ги правиме сами. Кратки партии, вистински противници, ништо за инсталирање.',
            },
            nav: {aria: 'Главна навигација', ourGames: 'Нашите игри'},
            hero: {
                titleLine1: 'Избери игра.',
                titleLine2: 'Играј веднаш.',
                cta: 'Види ги нашите игри',
            },
            games: {
                heading: 'Нашите игри',
            },
            boardbash: {
                logoAlt: 'Логото на Board Bash',
                tagline: 'Класични игри на табла, кратки партии',
                body: 'Икс-окс, Четири во ред, Точки и квадрати и Гомоку. Играј против компјутерот, предавај го уредот, или отвори соба и спротивстави се на пријател.',
                tagTicTacToe: 'Икс-окс',
                tagFourInARow: 'Четири во ред',
                tagDotsAndBoxes: 'Точки и квадрати',
                tagGomoku: 'Гомоку',
                play: 'Играј Board Bash',
                storeAria: 'Преземи Board Bash од Google Play',
                players: '1–2 играчи · онлајн и локално',
            },
            lagerhaus: {
                tagline: 'Складишна загатка',
                body: 'Сокобан, направен убаво. Потисни секоја кутија до своето место со што помалку движења, па однеси го резултатот на онлајн ранг-листата. Безброј уникатни нивоа – ниедна партија не е иста.',
                tagPuzzle: 'Загатка',
                tagSolo: 'Соло',
                tagLeaderboards: 'Ранг-листи',
                play: 'Играј Lagerhaus',
                storeAria: 'Преземи Lagerhaus од Google Play',
                players: '1 играч · веб и мобилен',
            },
            store: {badgeAlt: 'Преземи од Google Play'},
            workshop: {
                heading: 'Уште наши игри се во работилницата.',
                body: 'Играта стигнува тука само кога сме горди на неа, така што новите наслови излегуваат штом се готови за играње.',
            },
            footer: {
                games: 'Игри',
                privacy: 'Приватност',
                contact: 'Контакт',
                madeWith: 'Направено со ❤️ во',
                germanEngineered: 'Германско инженерство',
                australia: 'Австралија',
                legal: 'Google Play и логото на Google Play се трговски марки на Google LLC.',
            },
            contact: {
                title: 'Пиши ни',
                lede: 'Избери тема и твојата апликација за е-пошта ќе се отвори со пополнет наслов. Секоја порака ја чита вистински човек.',
                close: 'Затвори',
                feedbackTitle: 'Мислења или идеи',
                feedbackBody: 'Што ти се допаѓа, а што да смениме',
                bugTitle: 'Нешто не работи',
                bugBody: 'Кажи ни ја играта и уредот',
                dataTitle: 'Моите податоци',
                dataBody: 'Види или избриши што чуваме',
                orWrite: 'Или пиши ни директно:',
                copy: 'Копирај',
                copied: 'Копирано',
            },
            ui: {languageLabel: 'Јазик'},
        },
    },
};
